import { FeatureId } from '@upright/common/iam/access/feature-access/types';
import {
  useHasAccessToFeature,
  useIsClientOrTeamOrFreeTrial,
  useIsCompanyUser,
  useIsInvestorUser,
  useIsSelfSignupCompanyUser,
} from '@upright/ui/common/generic/hooks';

export type ClientType = 'self-signup-company' | 'company' | 'investor' | 'investor+company' | 'non-affiliated';
export type FrontPage = 'self-signup-company' | 'company' | 'investor' | 'non-affiliated';

/**
 * Used to decide what should be shown in route "/", and what pages are shown in the side bar.
 */
export function useFrontPageConfig() {
  const isCompanyClient = useIsCompanyUser();
  const isInvestorClient = useIsInvestorUser(); // Not in use yet for clients
  const isSelfSignupCompanyUser = useIsSelfSignupCompanyUser();
  const isClientOrTeamOrFreeTrial = useIsClientOrTeamOrFreeTrial();
  const screenerAccess = useHasAccessToFeature(FeatureId.SCREENER);

  const clientType = ((): ClientType => {
    if (isInvestorClient && isCompanyClient) {
      return 'investor+company';
    }
    if (isInvestorClient) {
      return 'investor';
    }
    if (isCompanyClient) {
      return 'company';
    }
    /**
     * OA: As of 10/21, self signup users are not nested under other company platform user groups. If we want to
     * change this, we may have to revise this order.
     */
    if (isSelfSignupCompanyUser) {
      return 'self-signup-company';
    }
    // Fallback for investor clients that have not been moved to proper investor user group.
    if (isClientOrTeamOrFreeTrial) {
      return 'investor';
    }
    return 'non-affiliated';
  })();

  const frontPage = ((): FrontPage => {
    if (clientType === 'investor' || clientType === 'investor+company') {
      return 'investor';
    }
    if (clientType === 'self-signup-company') {
      return 'self-signup-company';
    }
    if (clientType === 'company') {
      return 'company';
    }
    return 'non-affiliated';
  })();

  return {
    clientType,
    frontPage,
    screenerAccess,
    showUpgradeButton: false,
  };
}

export function showInvestorHomeView(clientType: ClientType) {
  return clientType === 'investor' || clientType === 'investor+company';
}

export function showCompanyDashBoard(clientType: ClientType) {
  return clientType === 'company' || clientType === 'investor+company';
}

export function showSelfSignupCompanyDashboard(clientType: ClientType) {
  return clientType === 'self-signup-company';
}

export function showScreener(clientType: ClientType) {
  return clientType === 'company' || clientType === 'investor' || clientType === 'investor+company';
}

export function showSharedAssets(clientType: ClientType) {
  return clientType === 'company' || clientType === 'investor' || clientType === 'investor+company';
}
